import React from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import {
	Code,
	DataObject,
	FormatListBulleted,
	FormatListNumbered,
	FormatQuote,
	HorizontalRule,
	TableChart,
} from '@mui/icons-material';
import { TooltipWrapper } from '@/components/TiptapEditor/Toolbar';
import ToolbarLinkButton from '@/components/TiptapEditor/Toolbar/ToolbarLinkButton';
import ToolbarHeadingMenu from './ToolbarHeadingMenu';
import TableMenu from './TableMenu';

const COMMAND_GROUPS = [
	[
		{
			title: 'Bold',
			icon: <FormatBoldIcon />,
			run: (editor) => editor.chain().focus().toggleBold().run(),
		},
		{
			title: 'Italics',
			icon: <FormatItalicIcon />,
			run: (editor) => editor.chain().focus().toggleItalic().run(),
		},
		{
			title: 'Strikethrough',
			icon: <StrikethroughSIcon />,
			run: (editor) => editor.chain().focus().toggleStrike().run(),
		},
	],
	[
		{
			title: 'Numbered list',
			icon: <FormatListNumbered />,
			run: (editor) => editor.chain().focus().toggleOrderedList().run(),
		},
		{
			title: 'Bullet list',
			icon: <FormatListBulleted />,
			run: (editor) => editor.chain().focus().toggleBulletList().run(),
		},
	],
	// [
	// 	{
	// 		title: 'Quote',
	// 		icon: <FormatQuote />,
	// 		run: (editor) => editor.chain().focus().toggleBlockquote().run(),
	// 	},
	// 	{
	// 		title: 'Code',
	// 		icon: <Code />,
	// 		run: (editor) => editor.chain().focus().toggleCode().run(),
	// 	},
	// 	{
	// 		title: 'Code block',
	// 		icon: <DataObject />,
	// 		run: (editor) => editor.chain().focus().toggleCodeBlock().run(),
	// 	},
	// ],
	[
		{
			title: 'Add table',
			icon: <TableChart />,
			run: (editor) =>
				editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
		},
		{
			title: 'Horizontal line',
			icon: <HorizontalRule />,
			run: (editor) => editor.chain().focus().setHorizontalRule().run(),
		},
	],
];

const MarkdownToolbar = () => {
	const { editor } = useCurrentEditor();

	const { isTableActive } = useEditorState({
		editor,
		selector: (ctx) => ({
			isTableActive: ctx.editor?.isActive('table'),
		}),
	});

	if (!editor) return <></>;

	return (
		<Stack
			className='markdown-toolbar'
			spacing={1}
			sx={(theme) => ({
				padding: '6px',
				'& .MuiIconButton-root': {
					padding: '0px',
					transition: 'color 0.15s ease',

					'&:hover': {
						backgroundColor: 'transparent !important',
						color: theme.palette.primary.main,
					},

					'&.active': {
						color: theme.palette.primary.main,
					},

					'& .MuiTouchRipple-root': {
						display: 'none',
					},
				},
				'& .MuiSvgIcon-root': {
					fontSize: '18px',
				},
			})}
		>
			<Stack
				direction='row'
				rowGap={1}
				columnGap={2}
				useFlexGap
				sx={{ alignItems: 'center', flexWrap: 'wrap' }}
			>
				<TooltipWrapper title='Heading'>
					<ToolbarHeadingMenu />
				</TooltipWrapper>

				{COMMAND_GROUPS.map((group, index) => (
					<Stack key={index} direction='row' spacing={1}>
						{group.map(({ title, icon, run }) => (
							<TooltipWrapper key={title} title={title}>
								<IconButton size='small' onClick={() => run(editor)}>
									{icon}
								</IconButton>
							</TooltipWrapper>
						))}
					</Stack>
				))}

				<TooltipWrapper title='Link'>
					<ToolbarLinkButton />
				</TooltipWrapper>
			</Stack>
			{isTableActive && (
				<Box pt='0.25em' sx={{ borderTop: '1px solid rgb(230, 233, 242)' }}>
					<TableMenu />
				</Box>
			)}
		</Stack>
	);
};

export default MarkdownToolbar;
